import React, { useEffect, useState, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { fetchRootCategories, fetchChildren } from '../slices/categorySlice'

export default function CategoryPicker({ value, onChange, prefillPath }) {
  const dispatch = useDispatch()
  const { byId, childrenByParent, roots, loading } = useSelector((s) => s.categories || {})

  // Track selected chain of ids by level
  const [levels, setLevels] = useState([]) // array of selected ids per level

  // Reflect externally provided `value` (categoryId) into local level chain for display
  useEffect(() => {
    if (!value) return
    const n = byId[value]
    if (!n) return
    const chain = (Array.isArray(n.ancestors) ? n.ancestors.map(a => a._id) : []).concat([value])
    // Only update if different to avoid unnecessary re-renders
    const same = chain.length === levels.length && chain.every((id, idx) => id === levels[idx])
    if (!same) setLevels(chain)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'levels': it's this effect's own derived state, including it would self-trigger
  }, [value, byId])

  useEffect(() => {
    // Avoid redundant or rapid repeat root fetches
    const hasRoots = Array.isArray(roots) && roots.length > 0
    const hasRootChildren = Array.isArray(childrenByParent?.['null']) && childrenByParent['null'].length > 0
    if (!hasRoots && !hasRootChildren && !loading) {
      dispatch(fetchRootCategories())
    }
  }, [dispatch, roots, childrenByParent, loading])

  // Prefill from slugs path if provided and we have roots
  const prefilledRef = useRef(false)

  // Prefill chain from path of slugs by progressively loading children
  useEffect(() => {
    const run = async () => {
      if (prefilledRef.current) return
      if (!prefillPath || !Array.isArray(prefillPath) || prefillPath.length === 0) {
        prefilledRef.current = true
        return
      }
      // Only attempt prefill if no selection has been made and roots are present
      const rootIds = childrenByParent?.['null'] || roots || []
  if (!rootIds || rootIds.length === 0) return

      // Helper to find a child under a given parent whose slug matches target
      const findChildBySlug = (parentId, slug) => {
        const key = parentId == null ? 'null' : String(parentId)
        const childIds = childrenByParent?.[key] || []
        for (const id of childIds) {
          const node = byId[id]
          if (node?.slug === slug) return node
        }
        return null
      }

      const nextLevels = []
      let currentParentId = null

      // Ensure root children lists are available
      // childrenByParent['null'] is set on fetchRootCategories.fulfilled

      const MAX_STEPS = 20
      for (let i = 0; i < prefillPath.length && i < MAX_STEPS; i++) {
        const slug = prefillPath[i]
        if (!slug) break
        // If we don't yet have children for this parent (except for roots), load them
        if (i > 0 && currentParentId && !childrenByParent[String(currentParentId)]) {
          await dispatch(fetchChildren(currentParentId))
        }
        const node = findChildBySlug(currentParentId, slug)
        if (!node) {
          // Try to load children and retry once if missing
          if (currentParentId) {
            await dispatch(fetchChildren(currentParentId))
            const retry = findChildBySlug(currentParentId, slug)
            if (!retry) break
            nextLevels.push(retry._id)
            currentParentId = retry._id
          } else {
            // At root level, roots already loaded; if not found, abort
            break
          }
        } else {
          nextLevels.push(node._id)
          currentParentId = node._id
        }
        // Yield to UI thread to avoid blocking
        await new Promise((r) => setTimeout(r, 0))
      }

      if (nextLevels.length > 0) {
        setLevels(nextLevels)
        const finalId = nextLevels[nextLevels.length - 1]
        const finalNode = byId[finalId]
        if (finalNode?.isLeaf) {
          const pathSlugs = nextLevels.map((id) => byId[id]?.slug).filter(Boolean)
          onChange?.({ id: finalId, name: finalNode.name, path: pathSlugs })
        } else {
          onChange?.(null)
        }
        prefilledRef.current = true
      } else {
        // No matches; avoid retry loops
        prefilledRef.current = true
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'onChange': re-running on every parent re-render (new function identity) would fight the prefilledRef guard above
  }, [prefillPath, byId, childrenByParent, roots, dispatch])

  const childrenOf = (parentId) => {
    const key = parentId == null ? 'null' : String(parentId)
    const ids = childrenByParent?.[key] || []
    return ids.map((id) => byId[id]).filter(Boolean)
  }

  const handleSelect = (levelIdx, selectedId) => {
    const next = levels.slice(0, levelIdx)
    next[levelIdx] = selectedId || null
    setLevels(next)
    if (!selectedId) {
      onChange?.(null)
      return
    }
    const node = byId[selectedId]
    const pathSlugs = next.map((id) => byId[id]?.slug).filter(Boolean)
    // Emit for ANY selection (leaf or not) with isLeaf flag
    onChange?.({ id: selectedId, name: node?.name, path: pathSlugs, isLeaf: !!node?.isLeaf })
    // Load children in the background for next level (if not a leaf)
    if (!node?.isLeaf) { try { dispatch(fetchChildren(selectedId)) } catch (_) {} }
  }

  const renderLevel = (levelIdx, parentId, options) => (
    <Form.Control as='select'
      key={`lvl-${levelIdx}`}
      className='mb-2'
      value={levels[levelIdx] || ''}
      onChange={(e) => handleSelect(levelIdx, e.target.value || null)}
    >
      <option value=''>-- Select --</option>
      {options.map((n) => (
        <option key={n._id} value={n._id}>
          {n.name}
        </option>
      ))}
    </Form.Control>
  )

  const chain = []
  // Root level options
  const rootOptions = childrenOf(null)
  const rootNodes = rootOptions.length ? rootOptions : (Array.isArray(roots) ? roots : []).map((id) => byId[id]).filter(Boolean)
  chain.push(renderLevel(0, null, rootNodes))
  // Additional levels based on current selection
  for (let i = 0; i < levels.length; i++) {
    const sel = levels[i]
    if (!sel) break
    const kids = childrenOf(sel)
    if (kids.length > 0) {
      chain.push(renderLevel(i + 1, sel, kids))
    }
  }

  return <div>{chain}</div>
}
